import { AddInStorage } from '../storage/AddInStorage';
import { waitForOfficeInitialization, isOfficeReady } from './officeInitialization';

interface DialogOptions {
  url: string;
  height?: number;
  width?: number;
  onMessage?: (message: string) => void;
  onError?: (error: any) => void;
}

let dialog: Office.Dialog | null = null;

/**
 * Checks if the Office Dialog API is available in the current context
 * @returns boolean indicating whether the Dialog API is available
 */
const isDialogApiAvailable = (): boolean => typeof Office?.context?.ui?.displayDialogAsync === 'function';

/**
 * Checks if the Office Message Parent API is available in the current context
 * @returns boolean indicating whether the Message Parent API is available
 */
const isMessageParentAvailable = (includeHostCheck = false): boolean =>
  typeof Office?.context?.ui?.messageParent === 'function'
  && (includeHostCheck ? Office?.context?.host !== undefined : true);

const getDialogDimensions = (desiredWidth: number = 500, desiredHeight: number = 700) => {
  const screenWidth = window.screen.width;
  const screenHeight = window.screen.height;

  const widthPercent = Math.min(100, Math.round((desiredWidth / screenWidth) * 100));
  const heightPercent = Math.min(100, Math.round((desiredHeight / screenHeight) * 100));

  return { width: widthPercent, height: heightPercent };
};

const openDialog = async (options: DialogOptions): Promise<void> => {
  const {
    url,
    onMessage = () => { },
    onError = () => { },
  } = options;

  if (options.url === '') {
    return;
  }

  // Check if there's already an active dialog
  if (dialog) {
    console.log('A dialog is already open, closing it before opening a new one');
    try {
      dialog.close();
    } catch (error) {
      console.log('Error closing existing dialog:', error);
    } finally {
      dialog = null;
    }
  }

  try {
    // Wait for Office.js to be initialized
    if (!isOfficeReady()) {
      await waitForOfficeInitialization();
    }

    // Check if Dialog API is available
    if (!isDialogApiAvailable()) {
      const error = new Error('Dialog API is not available in this context');
      console.error(error.message);
      onError(error);
      return;
    }

    const dimensions = getDialogDimensions();
    Office.context.ui.displayDialogAsync(
      url,
      {
        ...dimensions,
        promptBeforeOpen: false,
        displayInIframe: false
      },
      (result: Office.AsyncResult<Office.Dialog>) => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error('Failed to open dialog:', result.error);
          onError(result.error);
          return;
        }

        dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived,
          (arg: { message: string | boolean; } | { error: number; }) => {
            if ('message' in arg) {
              const message = JSON.parse(arg.message as any);
              if (message.status === "success") {
                onMessage(message.data);
                closeDialog();
              } else if (message.status === "dialog_ready_for_token") {
                const idToken = AddInStorage.getItem("id_token") || "";
                if (dialog) {
                  dialog.messageChild(JSON.stringify({ type: 'idToken', token: idToken }));
                } else {
                  closeDialog();
                }
              } else {
                onError(message.data);
                closeDialog();
              }
            } else if ('error' in arg) {
              console.error(`Dialog message received with error ${arg.error}`);
            }
          });

        dialog.addEventHandler(Office.EventType.DialogEventReceived,
          () => {
            // ignore these events
          });
      }
    );
  } catch (error) {
    console.error('Error opening dialog:', error);
    onError(error);
  }
};

const closeDialog = (): void => {
  if (!dialog) return;

  try {
    dialog.close();
  } catch (error) {
    console.log('Dialog already closed');
  } finally {
    dialog = null;
  }
};

export { openDialog, closeDialog, isDialogApiAvailable, isMessageParentAvailable };
